import {
  MouseEvent,
  PointerEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CalendarRange,
  Clock,
  Globe,
  MapPin,
  Tag,
} from "@digital-twins/design-system/icons";
import { useTranslation } from "react-i18next";
import { SocialUniverseItem } from "@digital-twins/shared-types";
import { CreditIcon } from "@/components/icons/CreditIcon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { intlLocaleForUi } from "@/i18n";
import { SocialPlatformIcon } from "./SocialPlatformIcon";
import { formatSocialCredits } from "./socialCredits";

/** The header's "Update now" button: the page's own update, its own rules. */
export interface SocialTopicUpdateNow {
  isDisabled: boolean;
  /** Why it is disabled (cooldown, credits); null shows no tooltip. */
  reason: string | null;
  onUpdate: () => void;
}

interface SocialTopicHeaderMetaProps {
  universe: SocialUniverseItem;
  /** Opens the channels pane; absent shows no channels item. */
  onOpenChannels?: () => void;
}

const ICON_CLASS = "h-3.5 w-3.5 shrink-0";

function formatDay(
  iso: string,
  locale: string,
  withYear: boolean,
): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  try {
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      year: withYear ? "numeric" : undefined,
      timeZone: "UTC",
    }).format(date);
  } catch {
    return null;
  }
}

/**
 * The header facts line: market, languages, the Topic's period, cadence,
 * keywords and this month's credits, one muted weight, each with its icon,
 * dot separated. The keywords are one item ("8 keywords") whose list opens
 * on hover or click. The live status lives in the pill beside the title.
 */
export function SocialTopicHeaderMeta(props: SocialTopicHeaderMetaProps) {
  const { universe, onOpenChannels } = props;
  // The Topic's period (D23): the same window every count and chart reads.
  const { period } = universe;

  const { t, i18n } = useTranslation("app");
  const locale = intlLocaleForUi(i18n.language);

  const languages = useMemo(() => {
    if (universe.languages.length === 0) {
      return "";
    }
    try {
      const names = new Intl.DisplayNames([locale], {
        type: "language",
        fallback: "code",
      });
      const labels = universe.languages.map((code) => {
        try {
          return names.of(code) ?? code;
        } catch {
          return code;
        }
      });
      return new Intl.ListFormat(locale, {
        style: "long",
        type: "conjunction",
      }).format(labels);
    } catch {
      return universe.languages.join(", ");
    }
  }, [universe.languages, locale]);

  // The year shows only when the span crosses years or does not end this year.
  const windowText = useMemo(() => {
    const fromYear = new Date(period.from).getUTCFullYear();
    const toYear = new Date(period.to).getUTCFullYear();
    const withYear = fromYear !== toYear;
    const from = formatDay(period.from, locale, withYear);
    const to = formatDay(period.to, locale, withYear);
    if (!from || !to) {
      return null;
    }
    return from === to
      ? from
      : t("socialListening.topicPage.meta.window", { from, to });
  }, [period, locale, t]);

  const cadence = t(`socialListening.form.cadence.${universe.cadence}`);
  const channelCount = universe.channels.length;
  const keywordCount = universe.keywords.length;

  // Credits this month (S2): hidden while nothing was charged yet.
  const monthCredits =
    (universe.creditsThisMonth ?? 0) > 0
      ? (universe.creditsThisMonth as number)
      : null;

  return (
    <div data-testid="topic-header-meta">
      {/* Every item carries its leading dot; the row is pulled one gap (23px, the old 10px + dot + 10px) left
          and clipped, so the dot of an item that starts a line is cut off
          and no line (on any width) starts with a separator. */}
      <div className="mt-3.5 overflow-x-clip" data-testid="topic-meta-clip">
        <ul
          aria-label={t("socialListening.topicPage.meta.ariaLabel")}
          className="-ml-[23px] flex flex-wrap items-center gap-y-1.5 text-sm text-muted-foreground"
          data-testid="topic-meta-row"
        >
          {universe.market ? (
            <MetaItem testId="topic-meta-market">
              <MapPin className={ICON_CLASS} aria-hidden="true" />
              {universe.market}
            </MetaItem>
          ) : null}
          {languages ? (
            <MetaItem testId="topic-meta-languages">
              <Globe className={ICON_CLASS} aria-hidden="true" />
              {languages}
            </MetaItem>
          ) : null}
          {windowText ? (
            <MetaItem testId="topic-meta-window">
              <CalendarRange className={ICON_CLASS} aria-hidden="true" />
              {windowText}
            </MetaItem>
          ) : null}
          <MetaItem testId="topic-meta-cadence">
            <Clock className={ICON_CLASS} aria-hidden="true" />
            {cadence}
          </MetaItem>
          {keywordCount > 0 ? (
            <MetaItem testId="topic-meta-keywords">
              <KeywordsPopover
                keywords={universe.keywords}
                label={t("socialListening.topicPage.meta.keywords", {
                  count: keywordCount,
                  value: keywordCount.toLocaleString(locale),
                })}
                ariaLabel={t("socialListening.topicPage.meta.keywordsAria")}
              />
            </MetaItem>
          ) : null}
          {onOpenChannels && channelCount > 0 ? (
            <MetaItem testId="topic-meta-channels">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-sm underline decoration-dotted decoration-muted-foreground/50 underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="topic-meta-channels-trigger"
                onClick={onOpenChannels}
              >
                <span className="inline-flex items-center gap-1" aria-hidden="true">
                  {universe.channels.map((channel) => (
                    <SocialPlatformIcon
                      key={channel.trackerToken}
                      platform={channel.platform}
                      className={ICON_CLASS}
                    />
                  ))}
                </span>
                {t("socialListening.topicPage.meta.channels", {
                  count: channelCount,
                  value: channelCount.toLocaleString(locale),
                })}
              </button>
            </MetaItem>
          ) : null}
          {monthCredits !== null ? (
            <MetaItem testId="topic-meta-credits">
              <CreditIcon className={ICON_CLASS} />
              <span className="tabular-nums">
                {t("socialListening.credits.topic.month", {
                  count: monthCredits,
                  value: formatSocialCredits(monthCredits, i18n.language),
                })}
              </span>
            </MetaItem>
          ) : null}
        </ul>
      </div>
    </div>
  );
}

interface MetaItemProps {
  testId: string;
  children: ReactNode;
}

function MetaItem(props: MetaItemProps) {
  const { testId, children } = props;

  return (
    <li
      className="relative ml-[23px] inline-flex items-center gap-1.5 before:absolute before:-left-[13px] before:top-1/2 before:h-[3px] before:w-[3px] before:-translate-y-1/2 before:rounded-full before:bg-muted-foreground/60 before:content-['']"
      data-testid={testId}
    >
      {children}
    </li>
  );
}

interface KeywordsPopoverProps {
  keywords: Array<string>;
  label: string;
  ariaLabel: string;
}

/** Hover delay before the list closes, so the pointer can cross into it. */
const CLOSE_DELAY_MS = 120;

/** "8 keywords": the list opens on hover (pointer) and on click or tap. */
function KeywordsPopover(props: KeywordsPopoverProps) {
  const { keywords, label, ariaLabel } = props;

  const [isOpen, setIsOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  // True while a mouse rests on the trigger or the list: a click then keeps it open.
  const isHovering = useRef(false);

  useEffect(() => {
    return () => {
      if (closeTimer.current !== null) {
        window.clearTimeout(closeTimer.current);
      }
    };
  }, []);

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const onPointerEnter = (event: PointerEvent) => {
    if (event.pointerType !== "mouse") {
      return;
    }
    cancelClose();
    isHovering.current = true;
    setIsOpen(true);
  };
  const onPointerLeave = (event: PointerEvent) => {
    if (event.pointerType !== "mouse") {
      return;
    }
    cancelClose();
    isHovering.current = false;
    closeTimer.current = window.setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
  };
  // Hover already opened it: the click must not toggle it shut. Touch and keyboard toggle.
  const onClick = (event: MouseEvent) => {
    event.preventDefault();
    setIsOpen((current) => (isHovering.current ? true : !current));
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        className="inline-flex items-center gap-1.5 rounded-sm underline decoration-dotted decoration-muted-foreground/50 underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid="topic-meta-keywords-trigger"
        onClick={onClick}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
      >
        <Tag className={ICON_CLASS} aria-hidden="true" />
        {label}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto max-w-[360px] p-3"
        data-testid="topic-meta-keywords-popover"
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <ul
          aria-label={ariaLabel}
          className="flex flex-wrap gap-1.5"
          data-testid="topic-keywords"
        >
          {keywords.map((keyword, index) => (
            <li
              key={keyword}
              className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground"
              data-testid={`topic-keyword-${index}`}
            >
              {keyword}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
